import React, { useEffect, useState } from "react";
import { addReceptionist, updateReceptionist } from "../utils/helperFunctions";
import { toast } from "react-toastify";

const ReceptionistFormModal = ({
  showModal,
  setShowModal,
  isEditing,
  // isResettingPassword,
  // handleResetPassword,
  fetchReceptionists,
  currentReceptionist,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [documents, setDocuments] = useState([]);
  const [receptionistDetails, setReceptionistDetails] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    role: "receptionist",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    documents: [],
  });

  useEffect(() => {
    if (currentReceptionist) {
      setReceptionistDetails({
        name: currentReceptionist.name || "",
        email: currentReceptionist.email || "",
        mobile: currentReceptionist.mobile || "",
        password: "",
        role: currentReceptionist.role || "receptionist",
        _id: currentReceptionist._id || "",
      });
      setDocuments(currentReceptionist.documents || []);
    } else {
      setReceptionistDetails({
        name: "",
        email: "",
        mobile: "",
        password: "",
        role: "receptionist",
      });
      setDocuments([]);
    }
    setErrorMsg("");
    setPreviewImage([]);
    setErrors({
      name: "",
      email: "",
      mobile: "",
      password: "",
      documents: [],
    });
  }, [currentReceptionist, showModal]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showModal) {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);


  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      email: "",
      mobile: "",
      password: "",
      documents: [],
    };

    if (!receptionistDetails.name?.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!receptionistDetails.email?.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(receptionistDetails.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!receptionistDetails.mobile?.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(receptionistDetails.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits";
      isValid = false;
    }

    if (!receptionistDetails.role?.trim()) {
      newErrors.role = "Role is required";
      isValid = false;
    }

    if (!isEditing && !receptionistDetails.password?.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    const docErrors = documents.map((doc, index) => {
      const docError = {};
      if (doc.documentType || doc.documentNumber || doc.file || doc.documentUrl) {
        if (!doc.documentType?.trim()) {
          docError.documentType = "Document type is required";
          isValid = false;
        }
        if (!doc.documentNumber?.trim()) {
          docError.documentNumber = "Document number is required";
          isValid = false;
        }
        if (!doc.file && !doc.documentUrl) {
          docError.file = "Document file is required";
          isValid = false;
        }
      }
      return docError;
    });

    newErrors.documents = docErrors;
    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReceptionistDetails((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setErrorMsg("");
  };

  const handleDocInputChange = (e, index) => {
    const { name, value } = e.target;
    setDocuments((prevDocs) => {
      const updated = [...prevDocs];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (!newErrors.documents[index]) {
        newErrors.documents[index] = {};
      }
      newErrors.documents[index][name] = "";
      return newErrors;
    });
    setErrorMsg("");
  };

  const handleFileChange = (e, index) => {
    const { files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      setDocuments((prevDocs) => {
        const updated = [...prevDocs];
        updated[index] = { ...updated[index], file };
        return updated;
      });
      setPreviewImage((prevImages) => {
        const updatedImages = [...prevImages];
        updatedImages[index] = URL.createObjectURL(file);
        return updatedImages;
      });
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (!newErrors.documents[index]) {
          newErrors.documents[index] = {};
        }
        newErrors.documents[index].file = "";
        return newErrors;
      });
      setErrorMsg("");
    }
  };

  const handleSaveReceptionist = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.keys(receptionistDetails).forEach((key) => {
        if (key !== "documents" && key !== "_id" && receptionistDetails[key]) {
          formData.append(key, receptionistDetails[key]);
        }
      });

      if (receptionistDetails._id) {
        formData.append("_id", receptionistDetails._id);
      }

      documents.forEach((doc, index) => {
        formData.append(`documents[${index}][documentType]`, doc.documentType || "");
        formData.append(`documents[${index}][documentNumber]`, doc.documentNumber || "");
        if (doc.file) {
          formData.append(`documents[${index}][documentUrl]`, doc.file);
        } else if (doc.documentUrl) {
          formData.append(`documents[${index}][documentUrl]`, doc.documentUrl);
        }
      });

      let response;
      if (isEditing) {
        response = await updateReceptionist(formData);
      } else {
        response = await addReceptionist(formData);
      }

      if (response.data.status) {
        toast.success(response.data.msg);
        fetchReceptionists();
        setShowModal(false);
        setPreviewImage([]);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.msg || "Failed to save receptionist");
      // console.error("Error saving receptionist:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setPreviewImage([]);
    setErrors({
      name: "",
      email: "",
      mobile: "",
      password: "",
      documents: [],
    });
    setErrorMsg("");
  };

  const roleOptions = [
    { value: "receptionist", label: "Receptionist" },
    { value: "view only", label: "View Only" }
  ];

  if (!showModal) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{
        overflow: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-500 opacity-75"
            onClick={handleCancel}
          ></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >

        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl w-full mx-2 sm:mx-0">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              {isEditing ? "Edit User" : "Add New User"}
            </h3>
            <form onSubmit={handleSaveReceptionist} className="space-y-4">
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 rounded-lg">
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={receptionistDetails.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={receptionistDetails.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile *
                    </label>
                    <input
                      type="text"
                      name="mobile"
                      value={receptionistDetails.mobile}
                      onChange={handleInputChange}
                      pattern="[0-9]{10}"
                      maxLength="10"
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                    {errors.mobile && (
                      <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role *
                    </label>
                    <select
                      name="role"
                      value={receptionistDetails.role}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.role && (
                      <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password {!isEditing && "*"}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={receptionistDetails.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required={!isEditing}
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 rounded-lg">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-700">
                  Documents
                </h3>
                {documents.map((doc, index) => (
                  <div
                    key={index}
                    className="mb-4 sm:mb-6 border border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
                  >
                    <div className="flex justify-between items-center mb-2 sm:mb-3">
                      <h4 className="font-medium text-xs sm:text-sm">
                        Document {index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          const updatedDocs = [...documents];
                          updatedDocs.splice(index, 1);
                          setDocuments(updatedDocs);
                          const updatedImages = [...previewImage];
                          updatedImages.splice(index, 1);
                          setPreviewImage(updatedImages);
                        }}
                        className="text-red-600 hover:text-red-800 text-xs sm:text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Type *
                        </label>
                        <input
                          type="text"
                          name="documentType"
                          value={doc.documentType || ""}
                          onChange={(e) => handleDocInputChange(e, index)}
                          className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          required
                        />
                        {errors.documents[index]?.documentType && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.documents[index].documentType}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Number *
                        </label>
                        <input
                          type="text"
                          name="documentNumber"
                          value={doc.documentNumber || ""}
                          onChange={(e) => handleDocInputChange(e, index)}
                          className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          required
                        />
                        {errors.documents[index]?.documentNumber && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.documents[index].documentNumber}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Upload Document *
                        </label>
                        <input
                          type="file"
                          onChange={(e) => handleFileChange(e, index)}
                          className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          accept=".jpg, .jpeg, .png"
                          required={!doc.documentUrl}
                        />
                        {errors.documents[index]?.file && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.documents[index].file}
                          </p>
                        )}
                      </div>
                    </div>

                    {(previewImage[index] || doc.documentUrl) && (
                      <div className="mt-2">
                        <img
                          src={
                            previewImage[index]
                              ? previewImage[index]
                              : `${process.env.REACT_APP_BASE_URL}/${doc.documentUrl}`
                          }
                          alt={`Document ${index + 1}`}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md border border-gray-300"
                        />
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setDocuments([...documents, {}])}
                    className="mt-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 focus:outline-none"
                  >
                    + Add Document
                  </button>
{/* 
                  {isEditing && (
                    <button
                      type="button"
                      // onClick={() => handleResetPassword(receptionistDetails._id)}
                      disabled={isResettingPassword}
                      className="mt-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 focus:outline-none"
                    >
                      {isResettingPassword ? "Resetting..." : "Reset Password"}
                    </button>
                  )} */}
                </div>
              </div>

              {errorMsg && (
                <div className="mt-4 text-red-600 text-sm font-medium">
                  {errorMsg}
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="mr-2 h-4 w-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {isEditing ? "Updating..." : "Adding..."}
                    </>
                  ) : isEditing ? (
                    "Update User"
                  ) : (
                    "Add User"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceptionistFormModal;